/**
 * xall-lander.js — LIVE PAID-OFFER VARIANT A behavior (subscription-only).
 * Current owner: docs/projects/mpc-lander/plan.md. This script is coupled to
 * sections/xall-lander-mpc-sub.liquid and offer_id=mpc-lander-v1. Do not extend it for a
 * sibling offer; give that offer an isolated script and identifier. The old
 * references/mpc-lander-render/xdc-logic.js is historical provenance only.
 *
 * Architecture: one IIFE. `state` holds everything that can change (supply, add-ons, FAQ
 * open map, gallery slide, cart/demo modal visibility). `derive(state)` recomputes every
 * display value the way xdc-logic.js's build() does (same constants, same rounding order).
 * `render()` writes derived values into the DOM by id/class — it never rebuilds static
 * content (reasons, steps, FAQ copy, testimonials, safety badges) which the section already
 * server-rendered in default-state form. Event delegation is used for the two repeated
 * dynamic groups (add-on tiles, FAQ items); singleton controls get direct listeners.
 * The only global is window.XallLander (debugging), matching the Quality Bars requirement.
 *
 * Pricing rounding model: per-unit cent rounding, mirroring Shopify selling-plan checkout
 * math. Shopify applies a percentage selling-plan discount to the $19.95 per-unit variant
 * price and rounds that per-unit result to the cent BEFORE multiplying by line quantity, so
 * every displayed subtotal here is computed the same way (round-per-unit, then multiply),
 * never by discounting a pre-multiplied pack total.
 */
(function () {
  'use strict';

  var ROOT = document.getElementById('xall-lander');
  if (!ROOT) return;

  var OFFER_ID = 'mpc-lander-v1';

  /* ------------------------------------------------------------------ */
  /* Config: read the JSON the section emitted (commerce IDs + asset URLs). */
  /* Must not error if the block is missing or the JSON is malformed —   */
  /* the lander products/plans may not exist yet (see plan doc §4).      */
  /* ------------------------------------------------------------------ */
  var CONFIG = { assets: {}, copy: {}, commerce: { products: {}, plans: {}, bonus: {} } };
  (function loadConfig() {
    var el = document.getElementById('xall-lander-data');
    if (!el) return;
    try {
      var parsed = JSON.parse(el.textContent || el.innerText || '{}');
      if (parsed && typeof parsed === 'object') {
        CONFIG.assets = parsed.assets || {};
        CONFIG.copy = parsed.copy || {};
        CONFIG.commerce = parsed.commerce || { products: {}, plans: {}, bonus: {} };
        CONFIG.commerce.products = CONFIG.commerce.products || {};
        CONFIG.commerce.plans = CONFIG.commerce.plans || {};
        CONFIG.commerce.bonus = CONFIG.commerce.bonus || {};
      }
    } catch (err) {
      console.warn('[xall-lander] could not parse config JSON, falling back to static content only:', err);
    }
  })();

  function copyText(key, fallback) {
    var value = CONFIG.copy && Object.prototype.hasOwnProperty.call(CONFIG.copy, key) ? CONFIG.copy[key] : null;
    return typeof value === 'string' && value.length ? value : fallback;
  }

  /* ------------------------------------------------------------------ */
  /* Offer constants — ported exactly from xdc-logic.js build('C').      */
  /* ------------------------------------------------------------------ */
  var PACK90 = 59.85;
  var PACK30 = 19.95;
  var ADDON = 19.95;
  var TIERS_90 = [0.25, 0.30, 0.35];
  var TIERS_30 = [0.15, 0.20, 0.25];
  var ADDON_IDS = ['cloths', 'toilet', 'washer'];
  var FREE_SHIPPING_DISCOUNT_CODE = 'MPC-LANDER-FREESHIP';

  var ADDON_META = {
    cloths: { name: copyText('addon_cloths_name', 'Multi-Use Cleaning Cloths · 6-Pack'), assetKey: 'addonCloths' },
    toilet: { name: copyText('addon_toilet_name', 'Foaming Toilet Cleaner'), assetKey: 'addonToilet' },
    washer: { name: copyText('addon_washer_name', 'Washing Machine Cleaner'), assetKey: 'addonWasher' }
  };

  var BONUS_ITEMS = [
    { id: 'bottle', name: copyText('bonus_bottle_name', 'Free Forever Bottle') },
    { id: 'guide', name: copyText('bonus_guide_name', 'Complete Cleaning Guide') }
  ];

  var DEMO_META = {
    cloths: {
      title: copyText('demo_cloths_title', 'Multi-Use Cleaning Cloths'),
      desc: copyText('demo_cloths_desc', 'One cloth replaces 250 paper towels — watch it soak up a full glass of liquid in a single wipe. Zero streaks, machine-washable 30+ times.'),
      assetKey: 'demoCloths'
    },
    toilet: {
      title: copyText('demo_toilet_title', 'Foaming Toilet Cleaner'),
      desc: copyText('demo_toilet_desc', 'Drop a sachet in the bowl and the foam climbs under the rim, dissolving rings and hard-water stains hands-free.'),
      assetKey: 'demoToilet'
    },
    washer: {
      title: copyText('demo_washer_title', 'Washing Machine Cleaner'),
      desc: copyText('demo_washer_desc', 'Watch a grimy washing-machine gasket go spotless in one cycle — it cleans the drum, hoses and seals you can’t reach.'),
      assetKey: 'demoWasher'
    }
  };

  // The wide hero keeps the established landscape media. The buy box uses its own
  // square, product-focused carousel so neither surface has to crop the other's art.
  var HERO_GALLERY_KEYS = ['reasonGrease', 'reasonSurfaces', 'reasonScent', 'review1After', 'review2After', 'review4After'];
  var BUYBOX_GALLERY_KEYS = ['buybox1', 'buybox2', 'buybox3', 'buybox4', 'buybox5', 'buybox6'];
  var BUYBOX_GALLERY_ALT = [
    'X-ALL Multi-Purpose Cleaner for soap scum, grease, hard-water stains, and multiple surfaces',
    'Three steps to mix and use X-ALL Multi-Purpose Cleaner',
    'X-ALL Multi-Purpose Cleaner loosening baked-on grease',
    'Shower corner before and after cleaning',
    'Oven before and after cleaning',
    'Bathtub before and after cleaning'
  ];

  var money = function (n) { return '$' + n.toFixed(2); };
  var round = function (n) { return Math.round(n * 100) / 100; };
  var toCents = function (n) { return Math.round(n * 100); };
  var fromCents = function (n) { return n / 100; };
  var discountedUnit = function (price, pct) {
    var discountPercent = Math.round(pct * 100);
    return fromCents(Math.round(toCents(price) * (100 - discountPercent) / 100));
  };
  var escapeHtml = function (value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  function asset(key, fallback) {
    return CONFIG.assets[key] || fallback || '';
  }

  /* ------------------------------------------------------------------ */
  /* Lazy video loading — GIF replacements ship as <video><source        */
  /* data-src> so nothing loads up front. promoteVideo() moves data-src  */
  /* to src and starts playback exactly once. Shared by the 4 inline     */
  /* "reason" videos (IntersectionObserver, wired below) and the         */
  /* demo-modal video (promoted on open only — see render()).            */
  /* ------------------------------------------------------------------ */
  function promoteVideo(video) {
    if (!video || video.getAttribute('data-xl-promoted') === 'true') return;
    var source = video.querySelector('source[data-src]');
    if (!source) return;
    source.setAttribute('src', source.getAttribute('data-src'));
    source.removeAttribute('data-src');
    video.setAttribute('data-xl-promoted', 'true');
    video.load();
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function (err) {
        console.warn('[xall-lander] video autoplay failed:', err);
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Analytics — thin wrapper: push to window.dataLayer (GTM/GA4) always, */
  /* forward to Mixpanel only if it's actually present. Never throws —   */
  /* a broken/missing analytics stack must not break the page.           */
  /* ------------------------------------------------------------------ */
  function track(name, props) {
    var payload = Object.assign({ offer_id: OFFER_ID }, props || {});
    try {
      window.dataLayer = window.dataLayer || [];
      var entry = { event: name };
      for (var key in payload) { if (Object.prototype.hasOwnProperty.call(payload, key)) entry[key] = payload[key]; }
      window.dataLayer.push(entry);
    } catch (err) {
      console.warn('[xall-lander] dataLayer push failed:', err);
    }
    try {
      if (window.mixpanel && typeof window.mixpanel.track === 'function') {
        window.mixpanel.track(name, payload);
      }
    } catch (err) {
      console.warn('[xall-lander] mixpanel track failed:', err);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Attribution capture — parse ad-platform query params once on load   */
  /* and persist them in sessionStorage so they survive the               */
  /* /cart/clear.js -> /cart/add.js -> /checkout hop. Read back in the    */
  /* checkout flow to stamp cart attributes right before redirecting.     */
  /* Plan doc §5 (tracking) + §7.3 (UTM/af_source conventions).           */
  /* ------------------------------------------------------------------ */
  var ATTRIBUTION_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'af_source', 'uid', 'oid', 'affid', 'sub1', 'sub2', 'sub3', 'sub4', 'sub5'
  ];
  var ATTRIBUTION_STORAGE_KEY = 'xall_lander_attribution';

  function captureAttribution() {
    try {
      var params = new URLSearchParams(window.location.search);
      var captured = {};
      ATTRIBUTION_PARAMS.forEach(function (key) {
        var value = params.get(key);
        if (value) captured[key] = value;
      });
      if (Object.keys(captured).length) {
        sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(captured));
      }
    } catch (err) {
      console.warn('[xall-lander] could not capture attribution params:', err);
    }
  }

  function getAttribution() {
    try {
      var raw = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      console.warn('[xall-lander] could not read stored attribution:', err);
      return {};
    }
  }

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */
  var state = {
    supply: '90', // '90' | '30'
    addons: {
      cloths: { on: false },
      toilet: { on: false },
      washer: { on: false }
    },
    faqOpen: {}, // index (string) -> boolean
    slide: 0,
    buyboxSlide: 0,
    testimonialSlide: 0,
    cartOpen: false,
    demoOpen: null // 'cloths' | 'toilet' | 'washer' | null
  };

  var countdownEnd = Date.now() + (6 * 3600 * 1000) + (35 * 60 * 1000) + (7 * 1000);

  // Tracks which add-on's demo video is currently loaded into the shared
  // modal <video> element, so render() only (re)promotes it when the demo
  // target actually changes — see the demo-modal block in render().
  var loadedDemoId = null;

  /* ------------------------------------------------------------------ */
  /* Derived values — mirrors xdc-logic.js build('C', themeC) exactly.   */
  /* ------------------------------------------------------------------ */
  function derive() {
    var sup = state.supply;
    var ad = state.addons;
    var onIds = ADDON_IDS.filter(function (id) { return ad[id].on; });
    var count = onIds.length;
    var addonQty = sup === '90' ? 3 : 1;
    var totalUnits = count * addonQty;
    var items = 1 + count;

    var tierArr = sup === '90' ? TIERS_90 : TIERS_30;
    var pctForOpt = function (o, it) { return (o === '90' ? TIERS_90 : TIERS_30)[Math.min(it, 3) - 1]; };
    var pctFor = function (it) { return pctForOpt(sup, it); };
    var pct = pctFor(items);
    var pctLabel = Math.round(pct * 100) + '%';

    // supUnits mirrors the checkout line quantity for the base pack (3× $19.95 units for
    // the 90-day option, 1× for 30-day) — see buildCartPayload()'s `quantity` below.
    var supUnits = function (o) { return o === '90' ? 3 : 1; };
    var supMonths = function (o) { return o === '90' ? 3 : 1; };
    var supRetail = function (o) { return o === '90' ? PACK30 * 3 : PACK30; };

    var perUnit = discountedUnit(ADDON, pct);
    var addonTot = fromCents(totalUnits * toCents(perUnit));
    // Shopify rounds each discounted unit to the nearest cent before multiplying by
    // quantity. Keep all line and order arithmetic in cents so the lander cannot drift
    // from the selling-plan allocation shown at checkout (notably the 30% tier).
    var supPerUnit = function (o) { return discountedUnit(PACK30, pctForOpt(o, items)); };
    var supTot = function (o) { return fromCents(supUnits(o) * toCents(supPerUnit(o))); };
    var total = fromCents(toCents(supTot(sup)) + toCents(addonTot));
    var cycleWord = sup === '90' ? 'every 12 weeks' : 'every 30 days';

    var supCardMo = function (o) { return money(round(supTot(o) / supMonths(o))); };
    var supSave = function (o) { return Math.round((1 - supTot(o) / supRetail(o)) * 100) + '%'; };

    var addonTiles = ADDON_IDS.map(function (id) {
      var qty = addonQty;
      var added = ad[id].on;
      var effPct = added ? pct : pctFor(items + 1);
      var unit = fromCents(toCents(discountedUnit(ADDON, effPct)) * qty);
      return {
        id: id,
        name: ADDON_META[id].name,
        added: added,
        qty: qty,
        unitLabel: '+' + money(unit),
        valueLabel: money(round(ADDON * qty)),
        qtyNote: sup === '90' ? (qty + (qty > 1 ? ' units' : ' unit') + ' · ' + (qty * 30) + '-day supply') : '',
        saveLine: added
          ? (copyText('addon_unlocks_prefix', 'Unlocks') + ' ' + Math.round(pct * 100) + '% ' + copyText('addon_unlocks_suffix', 'off everything'))
          : (copyText('addon_save_prefix', '+ ') + Math.round(pctFor(items + 1) * 100) + '% ' + copyText('addon_save_suffix', 'off your whole order'))
      };
    });

    var meterSegs = tierArr.map(function (p, i) {
      return { label: Math.round(p * 100) + '%', reached: items >= i + 1 };
    });

    var tierLabel = items >= 3
      ? copyText('tier_max_saver_name', 'Max Saver')
      : items === 2
        ? copyText('tier_super_saver_name', 'Super Saver')
        : copyText('tier_saver_name', 'Saver');
    var tierName = tierLabel + ' — ' + Math.round(pct * 100) + '% ' + copyText('tier_off_suffix', 'off');
    var nudge = items >= 3
      ? (copyText('nudge_max_prefix', 'Maximum discount unlocked —') + ' ' + pctLabel + ' ' + copyText('nudge_max_suffix', 'off your whole order, every delivery.'))
      : items === 2
        ? (copyText('nudge_two_prefix', 'Add one more product to unlock') + ' ' + Math.round(tierArr[2] * 100) + '% ' + copyText('nudge_two_suffix', 'off everything.'))
        : (copyText('nudge_one_prefix', 'Add any product to bump your whole order to') + ' ' + Math.round(tierArr[1] * 100) + '% ' + copyText('nudge_one_suffix', 'off.'));

    var giftLabels = [
      copyText('gift_1_label', '1ST REFILL'),
      copyText('gift_2_label', '2ND REFILL'),
      copyText('gift_3_label', '3RD REFILL')
    ];

    var valueRows = [
      { name: (sup === '90' ? '3' : '1') + '× ' + copyText('value_tablet_pack_label', 'X-ALL Tablet Pack'), was: money(supRetail(sup)), right: money(supTot(sup)), isFree: false }
    ];
    if (count > 0) {
      valueRows.push({
        name: totalUnits + '× ' + copyText(totalUnits > 1 ? 'value_bundle_addon_plural' : 'value_bundle_addon_singular', totalUnits > 1 ? 'Bundle Add-ons' : 'Bundle Add-on'),
        was: money(round(totalUnits * ADDON)),
        right: money(addonTot),
        isFree: false
      });
    }
    valueRows.push({ name: copyText('value_bottle_label', 'Reusable Spray Bottle'), was: '$12.99', right: copyText('value_free_label', 'FREE'), isFree: true });
    valueRows.push({ name: copyText('value_guide_label', 'Complete Cleaning Guide'), was: '$19.99', right: copyText('value_free_label', 'FREE'), isFree: true });
    valueRows.push({ name: copyText('value_shipping_label', 'Free Shipping'), was: '$9.95', right: copyText('value_free_label', 'FREE'), isFree: true });

    var totalValue = round(supRetail(sup) + totalUnits * ADDON + 12.99 + 19.99 + 9.95);

    return {
      sup: sup, items: items, pct: pct, pctLabel: pctLabel,
      sup90Mo: supCardMo('90'), sup30Mo: supCardMo('30'),
      sup90Billed: money(supTot('90')) + ' billed every 12 weeks · save ' + supSave('90'),
      sup30Billed: money(supTot('30')) + ' billed every 30 days',
      billedNote: money(total) + ' ' + copyText('billed_today_label', 'today') + ', ' + copyText('billed_then_label', 'then') + ' ' + money(total) + ' ' + cycleWord + ' · ' + copyText('billed_cancel_label', 'cancel anytime'),
      cartBaseQty: sup === '90' ? copyText('cart_90_supply_label', '90-day supply · ships every 12 wks') : copyText('cart_30_supply_label', '30-day supply · ships every 30 days'),
      cartBaseQtyBadge: copyText('cart_qty_prefix', 'QTY') + ' ' + (sup === '90' ? '3' : '1'),
      cartBaseImgKey: sup === '90' ? 'pack3' : 'pack1',
      cartBasePrice: money(supTot(sup)),
      cartDiscountLine: Math.round(pct * 100) + '% ' + copyText('cart_discount_suffix', 'off your whole order, every delivery'),
      giftLabels: giftLabels,
      addonTiles: addonTiles,
      meterSegs: meterSegs,
      tierName: tierName,
      nudge: nudge,
      savePctLabel: copyText('save_label_prefix', 'SAVE') + ' ' + pctLabel,
      valueRows: valueRows,
      totalValueLabel: copyText('value_total_prefix', 'Today’s kit value') + ' ' + money(totalValue) + ' — ' + copyText('value_total_pay_prefix', 'you pay') + ' ' + money(total) + '. ' + copyText('value_total_suffix', 'Refill gifts arrive later.'),
      youPay: money(total),
      total: total,
      tierIndex: Math.min(items, 3) - 1
    };
  }

  /* ------------------------------------------------------------------ */
  /* DOM refs                                                            */
  /* ------------------------------------------------------------------ */
  var el = {
    countdown: ROOT.querySelector('[data-xl="countdown"]'),
    heroImg: ROOT.querySelector('[data-xl="hero-slide-img"]'),
    heroVideo: ROOT.querySelector('[data-xl="hero-slide-video"]'),
    buyboxImg: ROOT.querySelector('[data-xl="buybox-slide-img"]'),
    buyboxVideo: ROOT.querySelector('[data-xl="buybox-slide-video"]'),
    dots: ROOT.querySelectorAll('[data-xl="dot"]'),
    thumbs: ROOT.querySelectorAll('[data-xl="thumb"]'),
    prevBtn: ROOT.querySelector('[data-xl="gallery-prev"]'),
    nextBtn: ROOT.querySelector('[data-xl="gallery-next"]'),
    testimonialSlides: ROOT.querySelectorAll('[data-xl-testimonial]'),
    testimonialDots: ROOT.querySelectorAll('[data-xl="testimonial-dot"]'),
    testimonialPrevBtn: ROOT.querySelector('[data-xl="testimonial-prev"]'),
    testimonialNextBtn: ROOT.querySelector('[data-xl="testimonial-next"]'),
    supplyCards: ROOT.querySelectorAll('[data-xl-supply]'),
    sup90Mo: ROOT.querySelector('[data-xl="sup90-mo"]'),
    sup30Mo: ROOT.querySelector('[data-xl="sup30-mo"]'),
    billedNote: ROOT.querySelector('[data-xl="billed-note"]'),
    giftLabels: ROOT.querySelectorAll('[data-xl-gift-label]'),
    valueStack: ROOT.querySelector('[data-xl="value-stack"]'),
    valueFootnote: ROOT.querySelector('[data-xl="value-footnote"]'),
    startNowBtn: ROOT.querySelector('[data-xl="open-cart"]'),
    faqItems: ROOT.querySelectorAll('[data-xl-faq]'),
    drawerScrim: ROOT.querySelector('[data-xl="drawer-scrim"]'),
    drawer: ROOT.querySelector('.xl-drawer'),
    drawerCloseBtns: ROOT.querySelectorAll('[data-xl="close-cart"]'),
    cartBaseImg: ROOT.querySelector('[data-xl="cart-base-img"]'),
    cartBaseBadge: ROOT.querySelector('[data-xl="cart-base-badge"]'),
    cartBaseQty: ROOT.querySelector('[data-xl="cart-base-qty"]'),
    cartBasePrice: ROOT.querySelector('[data-xl="cart-base-price"]'),
    cartDiscountLine: ROOT.querySelector('[data-xl="cart-discount-line"]'),
    meterCard: ROOT.querySelector('[data-xl="meter-card"]'),
    meterName: ROOT.querySelector('[data-xl="meter-name"]'),
    meterSave: ROOT.querySelector('[data-xl="meter-save"]'),
    meterSegs: ROOT.querySelectorAll('[data-xl-meter-seg]'),
    meterNudge: ROOT.querySelector('[data-xl="meter-nudge"]'),
    addonTiles: ROOT.querySelectorAll('[data-xl-addon]'),
    subtotal: ROOT.querySelector('[data-xl="subtotal"]'),
    checkoutBtn: ROOT.querySelector('[data-xl="checkout"]'),
    checkoutNotice: ROOT.querySelector('[data-xl="checkout-notice"]'),
    modalScrim: ROOT.querySelector('[data-xl="modal-scrim"]'),
    modalCloseBtns: ROOT.querySelectorAll('[data-xl="close-demo"]'),
    modalVideo: ROOT.querySelector('[data-xl="modal-video"]'),
    modalTitle: ROOT.querySelector('[data-xl="modal-title"]'),
    modalDesc: ROOT.querySelector('[data-xl="modal-desc"]'),
    lazyVideos: ROOT.querySelectorAll('[data-xl="lazy-video"]'),
    wordVideoTriggers: ROOT.querySelectorAll('[data-xl="word-video-open"]'),
    wordModalScrim: ROOT.querySelector('[data-xl="word-modal-scrim"]'),
    wordModalCloseBtn: ROOT.querySelector('[data-xl="close-word-video"]'),
    wordModalVideo: ROOT.querySelector('[data-xl="word-modal-video"]'),
    wordModalName: ROOT.querySelector('[data-xl="word-modal-name"]'),
    wordModalTitle: ROOT.querySelector('[data-xl="word-modal-title"]')
  };

  var drawerReturnFocus = null;

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */
  function renderTestimonials() {
    var count = el.testimonialSlides.length;
    if (!count) return;
    var idx = ((state.testimonialSlide % count) + count) % count;
    el.testimonialSlides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === idx); });
    el.testimonialDots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === idx); });
  }

  /* Gallery slides with an animated source (the render plays these as
     clips): key -> the CONFIG.assets key of the MP4. Everything else is a
     still. applyGalleryMedia() toggles between the img and its <video>
     sibling per slide. */
  var GALLERY_VIDEO_KEYS = {
    reasonGrease: 'reasonGreaseVideo',
    reasonSurfaces: 'reasonSurfacesVideo',
    buybox3: 'buybox3Video'
  };

  function applyGalleryMedia(img, video, key, stillUrl) {
    var videoKey = GALLERY_VIDEO_KEYS[key];
    var videoUrl = videoKey ? asset(videoKey) : '';
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (video && videoUrl && !reduceMotion) {
      if (video.getAttribute('data-xl-src') !== videoUrl) {
        video.src = videoUrl;
        video.setAttribute('data-xl-src', videoUrl);
      }
      video.hidden = false;
      if (img) img.hidden = true;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
    } else {
      if (video) { video.pause(); video.hidden = true; }
      if (img && stillUrl) { img.hidden = false; img.src = stillUrl; }
    }
  }

  function renderGallery() {
    var heroIdx = ((state.slide % HERO_GALLERY_KEYS.length) + HERO_GALLERY_KEYS.length) % HERO_GALLERY_KEYS.length;
    var heroKey = HERO_GALLERY_KEYS[heroIdx];
    applyGalleryMedia(el.heroImg, el.heroVideo, heroKey, asset(heroKey));
    el.dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === heroIdx); });

    var buyboxIdx = ((state.buyboxSlide % BUYBOX_GALLERY_KEYS.length) + BUYBOX_GALLERY_KEYS.length) % BUYBOX_GALLERY_KEYS.length;
    var buyboxKey = BUYBOX_GALLERY_KEYS[buyboxIdx];
    applyGalleryMedia(el.buyboxImg, el.buyboxVideo, buyboxKey, asset(buyboxKey));
    if (el.buyboxImg) el.buyboxImg.alt = BUYBOX_GALLERY_ALT[buyboxIdx] || 'X-ALL Multi-Purpose Cleaner';
    if (el.buyboxVideo) el.buyboxVideo.setAttribute('aria-label', BUYBOX_GALLERY_ALT[buyboxIdx] || 'X-ALL Multi-Purpose Cleaner in action');
    el.thumbs.forEach(function (thumb, i) { thumb.classList.toggle('is-active', i === buyboxIdx); });
  }

  function renderCountdown() {
    var remaining = Math.max(0, countdownEnd - Date.now());
    var hh = String(Math.floor(remaining / 3600000)).padStart(2, '0'); remaining %= 3600000;
    var mm = String(Math.floor(remaining / 60000)).padStart(2, '0'); remaining %= 60000;
    var ss = String(Math.floor(remaining / 1000)).padStart(2, '0');
    if (el.countdown) el.countdown.textContent = hh + ':' + mm + ':' + ss;
  }

  function renderValueStack(d) {
    if (!el.valueStack) return;
    el.valueStack.innerHTML = d.valueRows.map(function (row) {
      return (
        '<div class="xl-value-row">' +
          '<span class="xl-value-row__name">' + escapeHtml(row.name) + '</span>' +
          '<span class="xl-value-row__price">' +
            '<span class="xl-value-row__was">' + escapeHtml(row.was) + '</span> ' +
            '<span class="xl-value-row__right' + (row.isFree ? ' is-free' : '') + '">' + escapeHtml(row.right) + '</span>' +
          '</span>' +
        '</div>'
      );
    }).join('') +
    '<div class="xl-value-total">' +
      '<span class="xl-value-total__label">' + escapeHtml(copyText('value_pay_label', 'You pay today')) + '</span>' +
      '<span class="xl-value-total__amount">' + escapeHtml(d.youPay) + '</span>' +
    '</div>';
    if (el.valueFootnote) el.valueFootnote.textContent = d.totalValueLabel;
  }

  function render() {
    var d = derive();

    // Supply cards
    el.supplyCards.forEach(function (card) {
      var v = card.getAttribute('data-xl-supply');
      card.classList.toggle('is-selected', v === state.supply);
      card.setAttribute('aria-pressed', v === state.supply ? 'true' : 'false');
    });
    if (el.sup90Mo) el.sup90Mo.textContent = d.sup90Mo;
    if (el.sup30Mo) el.sup30Mo.textContent = d.sup30Mo;
    if (el.billedNote) el.billedNote.textContent = d.billedNote;

    // Gift ladder labels start at the first rebill. Appstle order 1 is checkout,
    // order 2 is the first refill.
    el.giftLabels.forEach(function (labelEl) {
      var i = Number(labelEl.getAttribute('data-xl-gift-label')) - 1;
      labelEl.textContent = d.giftLabels[i] || '';
    });

    // Cart base line
    if (el.cartBaseImg) el.cartBaseImg.src = asset(d.cartBaseImgKey);
    if (el.cartBaseBadge) el.cartBaseBadge.textContent = d.cartBaseQtyBadge;
    if (el.cartBaseQty) el.cartBaseQty.textContent = d.cartBaseQty;
    if (el.cartBasePrice) el.cartBasePrice.textContent = d.cartBasePrice;
    if (el.cartDiscountLine) el.cartDiscountLine.textContent = copyText('cart_discount_unlocked_prefix', 'You’ve unlocked') + ' ' + d.cartDiscountLine;

    // Meter
    if (el.meterName) el.meterName.textContent = d.tierName;
    if (el.meterSave) el.meterSave.textContent = d.savePctLabel;
    if (el.meterNudge) el.meterNudge.textContent = d.nudge;
    el.meterSegs.forEach(function (seg, i) {
      var data = d.meterSegs[i];
      if (!data) return;
      seg.classList.toggle('is-reached', data.reached);
      var label = seg.querySelector('[data-xl="meter-seg-label"]');
      if (label) label.textContent = data.label;
    });

    // Add-on tiles
    el.addonTiles.forEach(function (tile) {
      var id = tile.getAttribute('data-xl-addon');
      var data = d.addonTiles.filter(function (t) { return t.id === id; })[0];
      if (!data) return;
      tile.classList.toggle('is-added', data.added);
      var unitEl = tile.querySelector('[data-xl="addon-unit"]');
      var valueEl = tile.querySelector('[data-xl="addon-value"]');
      var noteEl = tile.querySelector('[data-xl="addon-note"]');
      var saveEl = tile.querySelector('[data-xl="addon-save"]');
      var toggleEl = tile.querySelector('[data-xl="addon-toggle"]');
      if (unitEl) unitEl.textContent = data.unitLabel;
      if (valueEl) valueEl.textContent = data.valueLabel;
      if (noteEl) { noteEl.textContent = data.qtyNote; noteEl.hidden = !data.qtyNote; }
      if (saveEl) saveEl.textContent = data.saveLine;
      if (toggleEl) {
        toggleEl.classList.toggle('is-added', data.added);
        toggleEl.textContent = data.added ? copyText('addon_added_label', 'Added ✓') : copyText('addon_add_label', 'Add to order');
      }
    });

    renderValueStack(d);

    if (el.subtotal) el.subtotal.textContent = d.youPay;

    renderGallery();
    renderTestimonials();

    // Cart drawer visibility + scroll lock
    if (el.drawerScrim) {
      el.drawerScrim.classList.toggle('is-open', state.cartOpen);
      el.drawerScrim.setAttribute('aria-hidden', state.cartOpen ? 'false' : 'true');
    }
    document.body.classList.toggle('xl-scroll-lock', state.cartOpen || !!state.demoOpen);

    // Demo modal — the video's <source> only ever gets a data-src assigned
    // when a demo opens (never up front); promoteVideo() moves it to src
    // and starts playback right then. Guarded by loadedDemoId so an
    // unrelated re-render while the modal is open (e.g. stepping an add-on
    // qty in the drawer behind it) doesn't restart the video from scratch.
    if (el.modalScrim) {
      var open = !!state.demoOpen;
      el.modalScrim.classList.toggle('is-open', open);
      if (open) {
        var meta = DEMO_META[state.demoOpen];
        if (meta && el.modalVideo) {
          if (el.modalTitle) el.modalTitle.textContent = meta.title;
          if (el.modalDesc) el.modalDesc.textContent = meta.desc;
          if (loadedDemoId !== state.demoOpen) {
            el.modalVideo.setAttribute('aria-label', meta.title);
            var modalSource = el.modalVideo.querySelector('source');
            if (modalSource) modalSource.setAttribute('data-src', asset(meta.assetKey));
            el.modalVideo.removeAttribute('data-xl-promoted');
            promoteVideo(el.modalVideo);
            loadedDemoId = state.demoOpen;
          }
        }
      } else if (el.modalVideo) {
        el.modalVideo.pause();
        loadedDemoId = null;
      }
    }

    return d;
  }

  /* ------------------------------------------------------------------ */
  /* State mutators — named to mirror xdc-logic.js's Component methods.  */
  /* ------------------------------------------------------------------ */
  function setSupply(s) {
    if (state.supply === s) return;
    state.supply = s;
    render();
    track('lander_supply_select', { supply: s });
  }

  function toggleAddon(id) {
    state.addons[id].on = !state.addons[id].on;
    var d = render();
    track('lander_addon_toggle', { addon: id, on: state.addons[id].on, tierPct: Math.round(d.pct * 100) });
  }

  function toggleFaq(item) {
    var idx = item.getAttribute('data-xl-faq');
    var open = !state.faqOpen[idx];
    state.faqOpen[idx] = open;
    var btn = item.querySelector('[data-xl="faq-toggle"]');
    var panel = item.querySelector('[data-xl="faq-answer"]');
    var sign = item.querySelector('[data-xl="faq-sign"]');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.hidden = !open;
    if (sign) sign.textContent = open ? '–' : '+';
    if (open) track('lander_faq_open', { index: Number(idx) });
  }

  function shiftSlide(delta) {
    state.slide = state.slide + delta;
    renderGallery();
  }

  function setSlide(i) {
    state.slide = i;
    renderGallery();
  }

  function setBuyboxSlide(i) {
    state.buyboxSlide = i;
    renderGallery();
  }

  function shiftTestimonial(delta) {
    state.testimonialSlide = state.testimonialSlide + delta;
    renderTestimonials();
  }

  function setTestimonial(i) {
    state.testimonialSlide = i;
    renderTestimonials();
  }

  function setBackgroundInert(value) {
    var card = ROOT.querySelector('.xl-card');
    var sticky = ROOT.querySelector('.xl-sticky-bar');
    [card, sticky].forEach(function (node) {
      if (!node) return;
      if (value) node.setAttribute('inert', '');
      else node.removeAttribute('inert');
    });
  }

  function openCart() {
    drawerReturnFocus = document.activeElement;
    state.cartOpen = true;
    render();
    setBackgroundInert(true);
    window.setTimeout(function () {
      var close = el.drawerCloseBtns && el.drawerCloseBtns[0];
      if (close) close.focus();
    }, 0);
    track('lander_drawer_open', {});
  }

  function closeCart() {
    if (!state.cartOpen) return;
    state.cartOpen = false;
    render();
    setBackgroundInert(false);
    if (drawerReturnFocus && typeof drawerReturnFocus.focus === 'function') drawerReturnFocus.focus();
    drawerReturnFocus = null;
  }

  function trapDrawerFocus(event) {
    if (!state.cartOpen || event.key !== 'Tab' || !el.drawer) return;
    var focusable = Array.prototype.slice.call(el.drawer.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (node) { return node.offsetParent !== null; });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }
  function openDemo(id) { state.demoOpen = id; render(); }
  function closeDemo() { state.demoOpen = null; render(); }

  /* ------------------------------------------------------------------ */
  /* Word-of-mouth video modal ("THE WORD IS SPREADING" carousel). Kept   */
  /* out of the central state/derive/render() machine on purpose — it     */
  /* carries no pricing-dependent data, just whichever card was clicked — */
  /* so it's a small self-contained open/close pair. Reuses promoteVideo()*/
  /* (same lazy-source-swap used by the demo modal) to load the real clip */
  /* only when the modal actually opens.                                  */
  /* ------------------------------------------------------------------ */
  var wordVideoReturnFocus = null;

  function openWordVideo(trigger) {
    if (!el.wordModalScrim || !el.wordModalVideo || !trigger) return;
    var name = trigger.getAttribute('data-xl-word-name') || '';
    var title = trigger.getAttribute('data-xl-word-title') || '';
    var src = trigger.getAttribute('data-xl-word-src') || '';
    var poster = trigger.getAttribute('data-xl-word-poster') || '';

    wordVideoReturnFocus = trigger;
    wordMarqueePaused = true;

    if (el.wordModalName) el.wordModalName.textContent = name;
    if (el.wordModalTitle) el.wordModalTitle.textContent = title;
    if (poster) el.wordModalVideo.setAttribute('poster', poster);
    el.wordModalVideo.setAttribute('aria-label', 'Video testimonial');

    var source = el.wordModalVideo.querySelector('source');
    if (source) source.setAttribute('data-src', src);
    el.wordModalVideo.removeAttribute('data-xl-promoted');
    try { el.wordModalVideo.currentTime = 0; } catch (err) { /* not loaded yet — fine to ignore */ }
    promoteVideo(el.wordModalVideo);

    el.wordModalScrim.classList.add('is-open');
    el.wordModalScrim.setAttribute('aria-hidden', 'false');
    document.body.classList.add('xl-scroll-lock');
    if (el.wordModalCloseBtn) el.wordModalCloseBtn.focus();

    track('lander_word_video_open', { hasIdentityLabel: !!(name || title) });
  }

  function closeWordVideo() {
    wordMarqueePaused = false;
    if (!el.wordModalScrim) return;
    el.wordModalScrim.classList.remove('is-open');
    el.wordModalScrim.setAttribute('aria-hidden', 'true');
    if (!state.cartOpen && !state.demoOpen) document.body.classList.remove('xl-scroll-lock');
    if (el.wordModalVideo) el.wordModalVideo.pause();
    if (wordVideoReturnFocus && typeof wordVideoReturnFocus.focus === 'function') wordVideoReturnFocus.focus();
    wordVideoReturnFocus = null;
  }

  /* ------------------------------------------------------------------ */
  /* Checkout: /cart/clear.js -> /cart/add.js -> redirect. No theme cart, */
  /* no {% form 'product' %} anywhere on this page (avoids the Appstle    */
  /* helper embed auto-injecting into product forms — see plan doc §2.8). */
  /* The bottle + guide are added as one-time $0 line items, never as     */
  /* selling-plan lines, so Appstle only owns the recurring cleaner items. */
  /* ------------------------------------------------------------------ */
  function showCheckoutNotice(msg) {
    if (!el.checkoutNotice) return;
    el.checkoutNotice.textContent = msg;
    el.checkoutNotice.classList.add('is-visible');
  }

  function hideCheckoutNotice() {
    if (!el.checkoutNotice) return;
    el.checkoutNotice.classList.remove('is-visible');
  }

  // Guards against double-clicking "Go to Checkout" while the
  // clear -> add -> attributes -> redirect chain is already running.
  var checkoutInFlight = false;
  var CHECKOUT_LOADING_LABEL = copyText('checkout_loading_label', 'Redirecting to checkout…');

  // Flips the checkout button (and body) into/out of the "leaving" state
  // used by assets/xall-lander-guards.css to keep third-party cart-drawer
  // embeds (UpCart et al.) from flashing open during the redirect.
  function setCheckoutRedirecting(active) {
    document.body.classList.toggle('xl-checkout-leaving', active);
    if (!el.checkoutBtn) return;
    el.checkoutBtn.disabled = active;
    el.checkoutBtn.setAttribute('aria-busy', active ? 'true' : 'false');
    if (active) {
      if (el.checkoutBtn.getAttribute('data-xl-original-label') === null) {
        el.checkoutBtn.setAttribute('data-xl-original-label', el.checkoutBtn.textContent);
      }
      el.checkoutBtn.textContent = CHECKOUT_LOADING_LABEL;
    } else {
      var original = el.checkoutBtn.getAttribute('data-xl-original-label');
      if (original !== null) el.checkoutBtn.textContent = original;
    }
  }

  function buildCartPayload(d) {
    var products = CONFIG.commerce.products;
    var plans = CONFIG.commerce.plans;
    var cadenceKey = state.supply === '90' ? '12week' : '30day';
    var tierIndex = d.tierIndex;

    var mpc = products.mpc;
    if (!mpc || !mpc.variantId) return { ok: false, reason: 'missing mpc product/variant' };
    var mpcPlanId = plans.mpc && plans.mpc[cadenceKey] ? plans.mpc[cadenceKey][tierIndex] : null;
    if (!mpcPlanId) return { ok: false, reason: 'missing mpc selling plan for ' + cadenceKey + ' tier ' + (tierIndex + 1) };

    var items = [{
      id: mpc.variantId,
      quantity: state.supply === '90' ? 3 : 1,
      selling_plan: mpcPlanId
    }];

    for (var i = 0; i < ADDON_IDS.length; i++) {
      var id = ADDON_IDS[i];
      if (!state.addons[id].on) continue;
      var product = products[id];
      if (!product || !product.variantId) return { ok: false, reason: 'missing ' + id + ' product/variant' };
      var planId = plans[id] && plans[id][cadenceKey] ? plans[id][cadenceKey][tierIndex] : null;
      if (!planId) return { ok: false, reason: 'missing ' + id + ' selling plan for ' + cadenceKey + ' tier ' + (tierIndex + 1) };
      items.push({ id: product.variantId, quantity: state.supply === '90' ? 3 : 1, selling_plan: planId });
    }

    var bonusItems = [];
    for (var j = 0; j < BONUS_ITEMS.length; j++) {
      var bonusMeta = BONUS_ITEMS[j];
      var bonusProduct = CONFIG.commerce.bonus[bonusMeta.id];
      if (!bonusProduct || !bonusProduct.variantId) return { ok: false, reason: 'missing ' + bonusMeta.id + ' bonus product/variant' };
      var bonusLine = {
        id: bonusProduct.variantId,
        quantity: 1,
        properties: {
          _xall_lander_bonus: 'true',
          _xall_lander_offer: OFFER_ID,
          _xall_lander_bonus_name: bonusMeta.name
        }
      };
      bonusItems.push(bonusLine);
      items.push(bonusLine);
    }

    return { ok: true, items: items, bonusItems: bonusItems };
  }

  function buildAttributionAttributes() {
    var captured = getAttribution();
    var attributes = {};
    for (var key in captured) {
      if (Object.prototype.hasOwnProperty.call(captured, key)) attributes[key] = captured[key];
    }
    attributes.offer_id = OFFER_ID;
    attributes.landing_page = OFFER_ID;
    return attributes;
  }

  // Stamps the captured attribution params (+ landing_page) onto the cart as
  // line-item-agnostic cart attributes. Resilience requirement: a failure
  // here must console.warn and NOT block checkout, so this always resolves
  // (never rejects) — callers just chain off it and move on.
  function setCartAttributes() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: buildAttributionAttributes() })
    }).catch(function (err) {
      console.warn('[xall-lander] setting cart attributes failed (continuing to checkout anyway):', err);
    });
  }

  function checkoutRedirectUrl() {
    return '/discount/' + encodeURIComponent(FREE_SHIPPING_DISCOUNT_CODE) + '?redirect=' + encodeURIComponent('/checkout');
  }

  function goToCheckout() {
    if (checkoutInFlight) return; // ignore double-clicks while a redirect is already running

    hideCheckoutNotice();
    var d = derive();
    var payload = buildCartPayload(d);

    if (!payload.ok) {
      console.warn('[xall-lander] checkout blocked, selling plan config incomplete:', payload.reason);
      showCheckoutNotice(copyText('checkout_blocked_notice', 'This kit isn’t ready to check out yet — please try again shortly or contact support.'));
      return;
    }

    checkoutInFlight = true;
    setCheckoutRedirecting(true);

    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (body) { throw new Error((body && body.description) || 'cart/clear.js failed'); });
        return res;
      })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload.items })
        });
      })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (body) { throw new Error((body && body.description) || 'cart/add.js failed'); });
        return res.json();
      })
      .then(function (cart) {
        // setCartAttributes() always resolves (see its own .catch), so a
        // failed attribute-set warns but never blocks this redirect.
        return setCartAttributes().then(function () { return cart; });
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('xall:lander:checkout', {
          detail: {
            items: payload.items,
            bonusItems: payload.bonusItems,
            supply: state.supply,
            total: d.total,
            itemCount: d.items,
            tierPct: Math.round(d.pct * 100),
            shippingDiscountCode: FREE_SHIPPING_DISCOUNT_CODE,
            cart: cart
          }
        }));
        window.location = checkoutRedirectUrl();
      })
      .catch(function (err) {
        console.warn('[xall-lander] checkout request failed:', err);
        showCheckoutNotice(copyText('checkout_error_notice', 'Something went wrong adding your kit to checkout — please try again.'));
        checkoutInFlight = false;
        setCheckoutRedirecting(false);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Event wiring                                                        */
  /* ------------------------------------------------------------------ */
  el.supplyCards.forEach(function (card) {
    card.addEventListener('click', function () { setSupply(card.getAttribute('data-xl-supply')); });
  });

  if (el.prevBtn) el.prevBtn.addEventListener('click', function () { shiftSlide(-1); });
  if (el.nextBtn) el.nextBtn.addEventListener('click', function () { shiftSlide(1); });
  el.dots.forEach(function (dot, i) { dot.addEventListener('click', function () { setSlide(i); }); });
  el.thumbs.forEach(function (thumb, i) { thumb.addEventListener('click', function () { setBuyboxSlide(i); }); });

  if (el.testimonialPrevBtn) el.testimonialPrevBtn.addEventListener('click', function () { shiftTestimonial(-1); });
  if (el.testimonialNextBtn) el.testimonialNextBtn.addEventListener('click', function () { shiftTestimonial(1); });
  el.testimonialDots.forEach(function (dot, i) { dot.addEventListener('click', function () { setTestimonial(i); }); });

  if (el.startNowBtn) el.startNowBtn.addEventListener('click', openCart);
  el.drawerCloseBtns.forEach(function (btn) { btn.addEventListener('click', closeCart); });
  if (el.drawerScrim) {
    el.drawerScrim.addEventListener('click', function (e) {
      if (e.target === el.drawerScrim) closeCart();
    });
  }

  el.modalCloseBtns.forEach(function (btn) { btn.addEventListener('click', closeDemo); });
  if (el.modalScrim) {
    el.modalScrim.addEventListener('click', function (e) {
      if (e.target === el.modalScrim) closeDemo();
    });
  }

  // Word-of-mouth video cards: each trigger carries its own src/poster/name/title
  // as data attributes (see the section markup), so no per-card JS config is needed.
  /* ------------------------------------------------------------------ */
  /* Sticky CTA bar visibility: show only after scrolling past the hero,  */
  /* and never while the buy box is on screen (owner call 2026-07-08).    */
  /* ------------------------------------------------------------------ */
  (function initStickyBar() {
    var bar = ROOT.querySelector('.xl-sticky-bar');
    var buybox = document.getElementById('offer-c');
    var hero = ROOT.querySelector('.xl-hero');
    if (!bar || !buybox || !hero) return;
    var buyboxVisible = false;
    function update() {
      var pastHero = window.scrollY > (hero.offsetTop + hero.offsetHeight);
      bar.classList.toggle('is-shown', pastHero && !buyboxVisible);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        buyboxVisible = entries[0] ? entries[0].isIntersecting : false;
        update();
      }, { rootMargin: '0px 0px -20% 0px' }).observe(buybox);
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  })();

  /* ------------------------------------------------------------------ */
  /* Back/forward cache recovery: browsers restore the exact DOM state    */
  /* the page left in (mid-redirect: scroll-locked, drawer open, button   */
  /* disabled, xl-checkout-leaving hiding fixed elements). Reset all      */
  /* transient state when the page is revived from bfcache.               */
  /* ------------------------------------------------------------------ */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    checkoutInFlight = false;
    setCheckoutRedirecting(false);
    if (state.cartOpen) closeCart();
    document.body.classList.remove('xl-scroll-lock');
    document.body.classList.remove('xl-checkout-leaving');
  });

  /* ------------------------------------------------------------------ */
  /* Scroll-reveal (motion set, 2026-07-08): fade-up sections as they     */
  /* enter the viewport. Progressive enhancement — targets are only       */
  /* visually deferred once html.xl-anim is set, and reduced-motion       */
  /* users never enter this branch at all. #offer-c excluded (sticky).    */
  /* ------------------------------------------------------------------ */
  (function initScrollReveal() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.documentElement.classList.add('xl-anim');
    var targets = [];
    ROOT.querySelectorAll('.xl-section, .xl-guarantee, .xl-final-cta, .xl-hero__proof-row').forEach(function (n) { targets.push(n); });
    var staggerGroups = ['.xl-reason', '.xl-step'];
    staggerGroups.forEach(function (sel) {
      ROOT.querySelectorAll(sel).forEach(function (n, i) {
        n.style.transitionDelay = Math.min(i * 70, 280) + 'ms';
        targets.push(n);
      });
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('xl-reveal-pending');
        entry.target.classList.add('xl-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    targets.forEach(function (n) {
      var r = n.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) return; /* already on screen: never hide */
      n.classList.add('xl-reveal-pending');
      io.observe(n);
    });
  })();

  /* ------------------------------------------------------------------ */
  /* Smooth anchor scrolling for CTA links: scrollIntoView (not CSS       */
  /* scroll-behavior) — verified working where window-level smooth        */
  /* scrolls get suppressed, and consistent across browsers. Falls back   */
  /* to an instant jump under prefers-reduced-motion.                     */
  /* ------------------------------------------------------------------ */
  ROOT.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
    if (!a) return;
    var id = a.getAttribute('href').slice(1);
    var target = id ? document.getElementById(id) : null;
    if (!target) return;
    e.preventDefault();
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* No hash write: fragment navigation cancels the in-flight smooth
       scroll in Chromium, and the URL hash serves no purpose here. */
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  });

  /* Delegated (not per-element) so marquee clones added later work too. */
  var wordStripEl = ROOT.querySelector('.xl-word-strip');
  if (wordStripEl) {
    wordStripEl.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest ? e.target.closest('[data-xl="word-video-open"]') : null;
      if (trigger) openWordVideo(trigger);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Word strip auto-scroll marquee (owner request 2026-07-08): scrolls  */
  /* horizontally on its own at a gentle speed. Seamless loop via a      */
  /* cloned card set; pauses on hover/touch/keyboard focus, while the    */
  /* word modal is open, when offscreen or tab-hidden; disabled entirely */
  /* under prefers-reduced-motion.                                       */
  /* ------------------------------------------------------------------ */
  var wordMarqueePaused = false;
  (function initWordMarquee() {
    var strip = wordStripEl;
    if (!strip || strip.children.length < 2) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    strip.style.scrollSnapType = 'none';
    strip.setAttribute('data-xl-marquee', 'true');

    Array.prototype.slice.call(strip.children).forEach(function (li) {
      var clone = li.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      Array.prototype.forEach.call(clone.querySelectorAll('button, a'), function (n) { n.setAttribute('tabindex', '-1'); });
      Array.prototype.forEach.call(clone.querySelectorAll('video[data-xl="lazy-video"]'), function (v) {
        var orig = li.querySelector('video[data-xl="lazy-video"]');
        v.removeAttribute('data-xl');
        var src = orig ? (orig.currentSrc || (orig.querySelector('source') && (orig.querySelector('source').src || orig.querySelector('source').getAttribute('data-src')))) : '';
        if (src) {
          v.innerHTML = '';
          v.src = src;
          v.muted = true;
          v.play && v.play().catch(function () {});
        }
      });
      strip.appendChild(clone);
    });

    var visible = true;
    var pos = strip.scrollLeft;
    var last = 0;
    var SPEED = 32; /* px per second — gentle */
    var idleTimer = null;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0] ? entries[0].isIntersecting : true;
      }, { rootMargin: '100px' }).observe(strip);
    }

    function pause() { wordMarqueePaused = true; }
    function resumeSoon(delay) {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { wordMarqueePaused = false; pos = strip.scrollLeft; }, delay || 2000);
    }
    strip.addEventListener('pointerenter', pause);
    strip.addEventListener('pointerleave', function () { resumeSoon(400); });
    strip.addEventListener('touchstart', pause, { passive: true });
    strip.addEventListener('touchend', function () { resumeSoon(2500); }, { passive: true });
    strip.addEventListener('focusin', pause);
    strip.addEventListener('focusout', function () { resumeSoon(400); });

    function step(ts) {
      if (!last) last = ts;
      var dt = Math.min(64, ts - last);
      last = ts;
      if (!wordMarqueePaused && visible && !document.hidden) {
        if (Math.abs(strip.scrollLeft - pos) > 2) pos = strip.scrollLeft; /* user scrolled manually */
        pos += SPEED * dt / 1000;
        var half = strip.scrollWidth / 2;
        if (half > 0 && pos >= half) pos -= half;
        strip.scrollLeft = pos;
      }
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  })();
  if (el.wordModalCloseBtn) el.wordModalCloseBtn.addEventListener('click', closeWordVideo);
  if (el.wordModalScrim) {
    el.wordModalScrim.addEventListener('click', function (e) {
      if (e.target === el.wordModalScrim) closeWordVideo();
    });
  }

  document.addEventListener('keydown', function (e) {
    trapDrawerFocus(e);
    if (e.key !== 'Escape') return;
    if (el.wordModalScrim && el.wordModalScrim.classList.contains('is-open')) { closeWordVideo(); return; }
    if (state.demoOpen) closeDemo();
    else if (state.cartOpen) closeCart();
  });

  // Event delegation: add-on tiles (toggle and demo trigger)
  ROOT.addEventListener('click', function (e) {
    var tile = e.target.closest('[data-xl-addon]');
    if (tile) {
      var id = tile.getAttribute('data-xl-addon');
      if (e.target.closest('[data-xl="addon-demo"]')) { openDemo(id); return; }
      if (e.target.closest('[data-xl="addon-toggle"]')) { toggleAddon(id); return; }
    }
  });

  // Event delegation: FAQ accordion
  el.faqItems.forEach(function (item) {
    var btn = item.querySelector('[data-xl="faq-toggle"]');
    if (btn) btn.addEventListener('click', function () { toggleFaq(item); });
  });

  if (el.checkoutBtn) el.checkoutBtn.addEventListener('click', goToCheckout);

  // Lazy-load the 4 inline "reason" videos (GIF replacements) as they near
  // the viewport instead of upfront — rootMargin gives a ~200px head start.
  if (el.lazyVideos && el.lazyVideos.length) {
    if ('IntersectionObserver' in window) {
      var lazyVideoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          promoteVideo(entry.target);
          lazyVideoObserver.unobserve(entry.target);
        });
      }, { rootMargin: '200px' });
      el.lazyVideos.forEach(function (video) { lazyVideoObserver.observe(video); });
    } else {
      // No IntersectionObserver support: fall back to loading immediately.
      el.lazyVideos.forEach(function (video) { promoteVideo(video); });
    }
  }

  // 'lander_checkout_click' is wired off the existing 'xall:lander:checkout'
  // CustomEvent (dispatched right before the /checkout redirect) instead of
  // duplicating the summary-building logic here.
  document.addEventListener('xall:lander:checkout', function (e) {
    var detail = (e && e.detail) || {};
    track('lander_checkout_click', {
      supply: detail.supply,
      itemCount: detail.itemCount,
      tierPct: detail.tierPct,
      total: detail.total
    });
  });

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */
  captureAttribution();
  render();
  renderCountdown();
  setInterval(renderCountdown, 1000);
  // The shared paid-offer controller emits the single canonical lander_view
  // after its live offer state and attribution are initialized.

  window.XallLander = {
    state: state,
    config: CONFIG,
    derive: derive,
    render: render,
    setSupply: setSupply,
    toggleAddon: toggleAddon,
    openCart: openCart,
    closeCart: closeCart,
    openDemo: openDemo,
    closeDemo: closeDemo,
    openWordVideo: openWordVideo,
    closeWordVideo: closeWordVideo,
    setTestimonial: setTestimonial,
    goToCheckout: goToCheckout,
    track: track,
    getAttribution: getAttribution
  };
})();
